import './App.css';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { handleApiErrorFor } from './api/apiClient';
import {
  DarkModeContextProvider,
  useDefaultDarkModeState,
  AuthContextProvider,
  useDefaultAuthState,
  useAuthContext,
  ToastProvider,
} from './contexts';
import Login from './features/Login';
import NotFound from './features/NotFound';
import DocumentListView from './features/DocumentListView/DocumentListView';
import AdminView from './features/AdminView/AdminView';
import ProtectedRoute from './components/ProtectedRoute';
import { storage } from './utils/storage';
import { AuthInfo } from './types';

// API errors are handled once per failed request here, not on every render of the hooks
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => handleApiErrorFor(error, query.meta),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => handleApiErrorFor(error, mutation.meta),
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false
    },
  },
});

function AppRoutes() {
  const { authInfo } = useAuthContext();
  const storedAuth = storage.getObject<AuthInfo>('portal_rs_auth');
  const userType = authInfo?.userType || storedAuth?.userType;
  const isAdmin = userType === 'ADMIN';

  return (
    <Routes>
      <Route path='/login' element={<Login />} />
      <Route path='/' element={
        <ProtectedRoute>
          {isAdmin ? <AdminView /> : <DocumentListView />}
        </ProtectedRoute>
      } />
      <Route path='*' element={<NotFound />} />
    </Routes>
  );
}

function App() {
  if (localStorage.getItem('darkMode') === null) {
    localStorage.setItem('darkMode', String(window.matchMedia("(prefers-color-scheme:dark)").matches));
  }

  const defaultDarkModeValue = useDefaultDarkModeState(localStorage.getItem('darkMode') === "true");
  const storedAuth = storage.getObject<AuthInfo>('portal_rs_auth');
  const defaultAuthData = useDefaultAuthState(storedAuth);

  return (
    <DarkModeContextProvider value={defaultDarkModeValue}>
      <AuthContextProvider value={defaultAuthData}>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </ToastProvider>
        </QueryClientProvider>
      </AuthContextProvider>
    </DarkModeContextProvider>
  );
}

export default App;
